export const CONFIG_IDS = {
  situs: "situs",
  main: "main",
  antares: "antares",
} as const;

export const STARTUP_CONFIG_IDS = {
  demo: CONFIG_IDS.main,
  widget: CONFIG_IDS.main,
} as const;

export const DEFAULT_WIDGET_ASSETS_BASE_URL = "https://zidle.skoumal.com/";

export const SCENE_DEFAULTS = {
  backgroundColor: "#F0EBEA",
  contactShadow: true,
  environmentMapIntensity: 1.3,
  arRealWorldScale: 1,
  textureMaxSize: 2048,
  textureAnisotropyLimit: 4,
  textureTileSize: 70,
  textureRepeat: 0.5,
  normalScale: 3,
  bumpScale: 3,
} as const;

// How the camera frames the loaded model in the viewer.
export const MODEL_FRAMING = {
  /**
   * Share of the frame's tighter axis the model's bounding sphere fills. At 1 the
   * sphere touches the frame edges, which for an upright object like a chair
   * still leaves a visible margin (the sphere is sized by the diagonal) — enough
   * for the toolbar overlaying the lower left. Lower it to pull the model back;
   * do not raise it above 1, or the model clips when orbited.
   */
  FILL: 1.3,
  /**
   * Raises the model in the frame by lowering the orbit target, as a share of the
   * model's bounding-sphere radius. 0 keeps the model centred; positive values
   * move it up, leaving the dead space at the bottom instead of the top.
   */
  LIFT: 0.12,
} as const;

/**
 * Stepped zoom driven by the viewer's +/- buttons. The mouse wheel deliberately
 * does not zoom: the widget is embedded in host pages, where swallowing the
 * wheel would trap the page scroll.
 *
 * Each step multiplies the framing distance by `STEP` (in) or divides by it
 * (out), so the reachable range is intentionally small — the model stays fully
 * framed at every step.
 */
export const MODEL_ZOOM = {
  /** Distance ratio between two neighbouring steps. */
  STEP: 0.88,
  /** Steps available towards the model, from the default framing. */
  IN_STEPS: 3,
  /** Steps available away from the model, from the default framing. */
  OUT_STEPS: 2,
} as const;

// Shared tail of every font stack: system fallbacks used until (or when) the
// configured family is unavailable.
const FONT_FALLBACKS =
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Ready-made stacks a config can pick from via `presentation.font`.
// Adding a family here is not enough on its own: the widget only ships the
// families bundled in `src/widget/fonts.ts`, and the demo app loads its fonts
// via the <link> tags in `index.html`. Both must cover any family listed here,
// otherwise the fallbacks above are used.
export const FONT_STACKS = {
  poppins: `"Poppins", ${FONT_FALLBACKS}`,
  exo2: `"Exo 2", ${FONT_FALLBACKS}`,
} as const;

// The value a config stores in `presentation.font`.
export type FontToken = keyof typeof FONT_STACKS;

export const PRESENTATION_DEFAULTS = {
  chrome: true,
  font: "poppins",
} as const satisfies { chrome: boolean; font: FontToken };
